import { Layout, Layouts } from 'react-grid-layout';
import {
  DashboardDto,
  LayoutDto,
  LayoutMutateDto,
  LayoutMutateDtoUpdate,
  WidgetDto,
} from '@thingslog/repositories';
import { QueryKeys } from '@thingslog/queries';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { dashboardLayoutQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';

interface UseDashboardLayoutOperationsProps {
  dashboardId: string;
  companyId?: string | number | null;
  widgets: WidgetDto[] | null;
  isEditable: boolean;
}

interface UseDashboardLayoutOperationsResult {
  layouts: Layouts | null;
  isDashboardLayoutEditable: boolean;
  isDashboardLayoutEditing: boolean;
  isPersistingDashboardLayout: boolean;
  isSaveDashboardLayoutDialogOpen: boolean;
  handleEditDashboardLayout: () => void;
  handleRequestSaveDashboardLayout: () => void;
  handleCloseSaveDashboardLayoutDialog: () => void;
  handleSaveDashboardLayout: () => void;
  handleLayoutChange: (_layout: Layout[], allLayouts: Layouts) => void;
}

const toGridLayout = (widget: WidgetDto, viewport: 'desktop' | 'mobile'): Layout => {
  const layout = viewport === 'desktop' ? widget.desktopLayout : widget.mobileLayout;

  return {
    i: widget.id,
    h: layout.h,
    w: layout.w,
    x: layout.x,
    y: layout.y,
    minW: layout.minW ?? undefined,
    minH: layout.minH ?? undefined,
    maxW: layout.maxW ?? undefined,
    maxH: layout.maxH ?? undefined,
  };
};

const toLayoutDto = (layout: Layout): LayoutDto => {
  return {
    h: layout.h,
    w: layout.w,
    x: layout.x,
    y: layout.y,
    minW: layout.minW ?? null,
    minH: layout.minH ?? null,
    maxW: layout.maxW ?? null,
    maxH: layout.maxH ?? null,
  };
};

const createDashboardLayouts = (widgets: WidgetDto[]): Layouts => {
  return {
    desktop: widgets.map((widget: WidgetDto): Layout => toGridLayout(widget, 'desktop')),
    mobile: widgets.map((widget: WidgetDto): Layout => toGridLayout(widget, 'mobile')),
  };
};

const createDashboardLayoutKey = (widgets: WidgetDto[]): string => {
  return widgets
    .map((widget: WidgetDto): string => {
      const desktop = widget.desktopLayout;
      const mobile = widget.mobileLayout;

      return [
        widget.id,
        desktop.h,
        desktop.w,
        desktop.x,
        desktop.y,
        desktop.minH,
        desktop.minW,
        desktop.maxH,
        desktop.maxW,
        mobile.h,
        mobile.w,
        mobile.x,
        mobile.y,
        mobile.minH,
        mobile.minW,
        mobile.maxH,
        mobile.maxW,
      ].join(':');
    })
    .join('|');
};

const createDashboardLayoutUpdates = (layouts: Layouts): LayoutMutateDtoUpdate[] | null => {
  const desktopLayouts = layouts.desktop ?? [];
  const mobileLayouts = layouts.mobile ?? [];

  if (desktopLayouts.length !== mobileLayouts.length) {
    return null;
  }

  const mobileLayoutByWidgetId = new Map<string, Layout>(
    mobileLayouts.map((layout: Layout): [string, Layout] => [layout.i, layout])
  );

  return desktopLayouts.reduce<LayoutMutateDtoUpdate[] | null>(
    (updates: LayoutMutateDtoUpdate[] | null, desktopLayout: Layout) => {
      if (updates === null) {
        return null;
      }

      const mobileLayout = mobileLayoutByWidgetId.get(desktopLayout.i);

      if (!mobileLayout) {
        return null;
      }

      updates.push({
        widgetId: desktopLayout.i,
        desktop: toLayoutDto(desktopLayout),
        mobile: toLayoutDto(mobileLayout),
      });

      return updates;
    },
    []
  );
};

export const useDashboardLayoutOperations = ({
  dashboardId,
  companyId,
  widgets,
  isEditable,
}: UseDashboardLayoutOperationsProps): UseDashboardLayoutOperationsResult => {
  const { useUpdateDashboardLayout } = dashboardLayoutQueryClient;
  const queryClient = useQueryClient();
  const [isEditingDashboardLayout, setIsEditingDashboardLayout] = useState(false);
  const [isSaveDashboardLayoutDialogOpen, setIsSaveDashboardLayoutDialogOpen] = useState(false);
  const [layouts, setLayouts] = useState<Layouts | null>(null);
  const [syncedLayoutKey, setSyncedLayoutKey] = useState<string | null>(null);

  const updateDashboardLayoutMutation = useUpdateDashboardLayout({
    onSuccess: (
      _dashboard: DashboardDto,
      variables: { id: string; body: LayoutMutateDto; companyId?: string | number | null }
    ): void => {
      queryClient.invalidateQueries([QueryKeys.GetDashboard, variables.id, variables.companyId]);
      queryClient.invalidateQueries([QueryKeys.GetWidgets, variables.id, variables.companyId]);
    },
  });

  const sourceLayoutKey = useMemo<string | null>(() => {
    return widgets ? createDashboardLayoutKey(widgets) : null;
  }, [widgets]);

  const sourceLayouts = useMemo<Layouts | null>(() => {
    return widgets ? createDashboardLayouts(widgets) : null;
  }, [widgets]);

  useEffect(() => {
    if (!sourceLayouts || !sourceLayoutKey) {
      setLayouts(null);
      setSyncedLayoutKey(null);
      setIsEditingDashboardLayout(false);
      setIsSaveDashboardLayoutDialogOpen(false);
      return;
    }

    if (sourceLayoutKey === syncedLayoutKey) {
      return;
    }

    setLayouts(sourceLayouts);
    setSyncedLayoutKey(sourceLayoutKey);
  }, [sourceLayoutKey, sourceLayouts, syncedLayoutKey]);

  useEffect(() => {
    if (isEditable) {
      return;
    }

    setIsEditingDashboardLayout(false);
    setIsSaveDashboardLayoutDialogOpen(false);
  }, [isEditable]);

  const handleEditDashboardLayout = useCallback((): void => {
    if (!isEditable) {
      return;
    }

    setLayouts((prev: Layouts | null): Layouts | null => prev ?? sourceLayouts);
    setIsEditingDashboardLayout(true);
  }, [isEditable, sourceLayouts]);

  const handleRequestSaveDashboardLayout = useCallback((): void => {
    setIsSaveDashboardLayoutDialogOpen(true);
  }, []);

  const handleCloseSaveDashboardLayoutDialog = useCallback((): void => {
    setIsSaveDashboardLayoutDialogOpen(false);
  }, []);

  const handleSaveDashboardLayout = useCallback((): void => {
    if (!dashboardId || !layouts) {
      return;
    }

    const updates = createDashboardLayoutUpdates(layouts);

    if (!updates) {
      return;
    }

    updateDashboardLayoutMutation.mutate({
      id: dashboardId,
      body: { updates },
      companyId,
    });

    setIsEditingDashboardLayout(false);
    setIsSaveDashboardLayoutDialogOpen(false);
  }, [dashboardId, layouts, companyId, updateDashboardLayoutMutation]);

  const handleLayoutChange = useCallback(
    (_layout: Layout[], allLayouts: Layouts): void => {
      if (!isEditingDashboardLayout) {
        return;
      }

      setLayouts(allLayouts);
    },
    [isEditingDashboardLayout]
  );

  const isDashboardLayoutEditing = isEditingDashboardLayout && isEditable;

  return {
    layouts,
    isDashboardLayoutEditable: isEditable,
    isDashboardLayoutEditing,
    isPersistingDashboardLayout: updateDashboardLayoutMutation.isLoading,
    isSaveDashboardLayoutDialogOpen,
    handleEditDashboardLayout,
    handleRequestSaveDashboardLayout,
    handleCloseSaveDashboardLayoutDialog,
    handleSaveDashboardLayout,
    handleLayoutChange,
  };
};
